import { PAGE_SCROLL_PADDING_PX, PAGE_SCROLL_SETTLE_PX } from './constants';

type PageJumpTargetOptions = {
  start: number;
  offset: number;
  maximum: number;
  pageNumber: number;
  padding?: number;
};

export function getPageJumpTarget({
  start,
  offset,
  maximum,
  pageNumber,
  padding = PAGE_SCROLL_PADDING_PX,
}: PageJumpTargetOptions) {
  const desired = pageNumber === 1 ? 0 : start + offset - padding;
  return Math.max(0, Math.min(desired, maximum));
}

export type ScrollToPageOptions = {
  container: HTMLElement;
  pageElement: HTMLElement;
  horizontal: boolean;
  pageNumber: number;
  behavior?: 'instant' | 'smooth';
  onDone: (animated: boolean) => void;
};

/**
 * Scrolls to a page and returns a cancellation function. Cancelling an active
 * smooth scroll removes its completion listener, stops it at the current
 * position, and prevents the stale `onDone` callback. Instant and unnecessary
 * jumps return a no-op function so callers can always use the same API.
 */
export function scrollToPage({
  container,
  pageElement,
  horizontal,
  pageNumber,
  behavior = 'smooth',
  onDone,
}: ScrollToPageOptions): () => void {
  const containerRect = container.getBoundingClientRect();
  const pageRect = pageElement.getBoundingClientRect();
  const start = horizontal ? container.scrollLeft : container.scrollTop;
  const offset = horizontal
    ? pageRect.left - containerRect.left
    : pageRect.top - containerRect.top;
  const maximum = horizontal
    ? container.scrollWidth - container.clientWidth
    : container.scrollHeight - container.clientHeight;
  const target = getPageJumpTarget({
    start,
    offset,
    maximum,
    pageNumber,
  });
  const change = target - start;

  if (behavior === 'instant') {
    if (horizontal) {
      container.scrollLeft = target;
    } else {
      container.scrollTop = target;
    }
    onDone(false);
    return () => undefined;
  }

  if (Math.abs(change) < PAGE_SCROLL_SETTLE_PX) {
    onDone(false);
    return () => undefined;
  }

  let active = true;
  const handleScrollEnd = () => {
    if (active) {
      active = false;
      onDone(true);
    }
  };
  container.addEventListener('scrollend', handleScrollEnd, { once: true });
  container.scrollTo({
    behavior: 'smooth',
    left: horizontal ? target : container.scrollLeft,
    top: horizontal ? container.scrollTop : target,
  });

  return () => {
    if (!active) {
      return;
    }
    active = false;
    container.removeEventListener('scrollend', handleScrollEnd);
    container.scrollTo({
      behavior: 'auto',
      left: container.scrollLeft,
      top: container.scrollTop,
    });
  };
}
